import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

export const legalEntityKeys = {
  all: ['legalEntities'],
  lists: () => [...legalEntityKeys.all, 'list'],
  collecting: () => [...legalEntityKeys.all, 'collecting'],
  details: () => [...legalEntityKeys.all, 'detail'],
  detail: (id) => [...legalEntityKeys.details(), id],
};

const LEGAL_ENTITY_PAYLOAD_FIELDS = [
  'cuit',
  'legal_business_name',
  'entity_type',
  'libb',
  'is_intermediate_entity',
  'membership_fee',
  'company',
  'address',
  'uuid_district',
  'postal_code',
  'phone_number',
  'email',
  'observations',
  'contact_information',
  'is_collecting_entity',
  'is_subcontractor',
  'is_provider',
];

const normalizeEntityType = (value) => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value !== 'string') {
    return value;
  }

  const normalized = value.trim();
  if (!normalized) {
    return undefined;
  }

  const enumName = normalized.split('.').pop().toLowerCase();
  const aliases = {
    provider: 'provider',
    intermediate_entity: 'intermediate_entity',
  };

  return aliases[enumName] || aliases[normalized.toLowerCase()] || normalized;
};

const sanitizeLegalEntityPayload = (legalEntityData) => {
  const payload = {};

  LEGAL_ENTITY_PAYLOAD_FIELDS.forEach((key) => {
    const value = legalEntityData[key];

    if (value === undefined || value === null) {
      return;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return;
      }

      payload[key] = key === 'entity_type' ? normalizeEntityType(trimmed) : trimmed;
      return;
    }

    payload[key] = value;
  });

  if (payload.entity_type === undefined) {
    delete payload.entity_type;
  }

  return payload;
};

/**
 * Hook para obtener todas las entidades legales
 */
export const useLegalEntities = () => {
  return useQuery({
    queryKey: legalEntityKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('legal_entities');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener entidades legales recaudadoras
 */
export const useRecLegalEntities = () => {
  return useQuery({
    queryKey: legalEntityKeys.collecting(),
    queryFn: async () => {
      const response = await apiClient.get('legal_entities/collecting');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener una entidad legal específica
 * @param {string} id - UUID de la entidad legal
 * @param {Object} options - Opciones adicionales para la query
 */
export const useLegalEntity = (id, options = {}) => {
  return useQuery({
    queryKey: legalEntityKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`legal_entities/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear una nueva entidad legal
 */
export const useCreateLegalEntity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (legalEntityData) => {
      const response = await apiClient.post(
        'legal_entities',
        sanitizeLegalEntityPayload(legalEntityData)
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: legalEntityKeys.lists() });
      queryClient.invalidateQueries({ queryKey: legalEntityKeys.collecting() });
    },
    onError: (error) => {
      console.error('Error creating legal entity:', error);
    },
  });
};

/**
 * Hook para actualizar una entidad legal existente
 */
export const useUpdateLegalEntity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (legalEntityData) => {
      const response = await apiClient.put(
        `legal_entities/${legalEntityData.uuid}`,
        sanitizeLegalEntityPayload(legalEntityData)
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(legalEntityKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: legalEntityKeys.lists() });
      queryClient.invalidateQueries({ queryKey: legalEntityKeys.collecting() });
    },
    onError: (error) => {
      console.error('Error updating legal entity:', error);
    },
  });
};

/**
 * Hook para eliminar una entidad legal
 */
export const useDeleteLegalEntity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`legal_entities/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: legalEntityKeys.lists() });
      queryClient.invalidateQueries({ queryKey: legalEntityKeys.collecting() });
      queryClient.removeQueries({ queryKey: legalEntityKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting legal entity:', error);
    },
  });
};
